import {
	computeContextMenuTitle,
	ContextMenu,
	GridView,
	LoadMoreIndicator,
	useContextMenu
} from "client/Components";
import {
	ReactElement,
	Ref,
	RefObject,
	forwardRef,
	useImperativeHandle,
	useMemo,
	useRef
} from "react";
import { DocumentGridViewItem } from "../DocumentGridViewItem";
import {
	MediaQueryKind,
	MultiSelectableOptions,
	SelectableItemOptions,
	useIsTouchDevice,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { GridItem as GridItemModel } from "./WebDavOverview";
import { GridViewGroupItem, GridViewItemKind } from "client/Components/GridView/groupGridViewItems";
import { FileSystemItemModel } from "client/Api/Documents/WebDav/webDav";
import { WebDavDocumentAction, WebDavDocumentActionContext } from "./actions";
import { DocumentGridViewContainer } from "../DocumentGridViewContainer";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import { DocumentNoResult } from "../DocumentNoResult";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export type WebDavGridViewProps = {
	isBusy: boolean;
	selectionFor: (item: GridItemModel) => SelectableItemOptions;
	items: readonly GridItemModel[];
	isInSelectMode: boolean;
	selectionActions: readonly WebDavDocumentAction[];
	actionContext: WebDavDocumentActionContext;
	multiSelection: MultiSelectableOptions;
	onEnableSelectMode(): void;
	onOpenItem: (item: FileSystemItemModel) => void;
	hasSearch: boolean;
};

export const WebDavGridView = forwardRef(
	(props: WebDavGridViewProps, ref: Ref<WebDavGridViewHandle>): ReactElement => {
		const {
			isBusy,
			multiSelection,
			selectionFor,
			items,
			onOpenItem,
			selectionActions,
			actionContext,
			isInSelectMode,
			onEnableSelectMode,
			hasSearch
		} = props;

		const gridRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
		const [contextMenuHandler, contextMenuProps] = useContextMenu(gridRef);
		const isTouch: boolean = useIsTouchDevice();
		const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

		const itemsToRender = useMemo(
			(): readonly GridViewGroupItem<GridItemModel>[] => {
				return items.map(
					x => ({
						model: x,
						uniqueKey: x.id,
						itemKind: x.kind === DocumentItemType.Folder
							? GridViewItemKind.Default
							: GridViewItemKind.Preview
					})
				);
			},
			[items]
		);

		useImperativeHandle(
			ref,
			() => ({
				resetScrollPosition: ():void => {
					gridRef.current
						?.scrollTo(0 , 0);
				}
			}),
			[]
		);

		if (!isBusy && items.length === 0) {
			return (
				<DocumentNoResult
					hasSearch={hasSearch}
				/>
			);
		}

		return (
			<DocumentGridViewContainer>
				<GridView
					trackingId="webdav-grid-view"
					ref={gridRef}
					items={itemsToRender}
					hasLassoSelection={!isMobile}
					multiSelection={multiSelection}
					elementToRenderAfterItems={
						<>
							<LoadMoreIndicator
								hasMoreItems={false}
								isLoadingData={isBusy}
								onRequestMoreItems={() => { /** Noop */ }}
							/>
							{itemsToRender.length > 0 && (
								<AiAssistantFabSpacing />
							)}
						</>
					}
				>
					{(item) => {
						const {
							itemKind,
							model,
							uniqueKey
						} = item;

						const {
							id,
							kind,
							name,
							model: webDavModel
						} = model;

						return (
							<DocumentGridViewItem
								key={uniqueKey}
								id={id}
								name={name}
								documentKind={kind}
								selectable={selectionFor(model)}
								selectionActions={selectionActions}
								actionContext={{...actionContext, selectedDocuments: [webDavModel]}}
								onContextMenu={contextMenuHandler}
								isInSelectMode={isInSelectMode}
								isMobile={isMobile}
								isTouch={isTouch}
								isPreview={itemKind === GridViewItemKind.Preview}
								onEnableSelectMode={onEnableSelectMode}
								onOpenItem={() => onOpenItem(webDavModel)}
								isBookmarked={Boolean(webDavModel.bookmarkId)}
							/>
						);
					}}
				</GridView>
				{contextMenuProps && (
					<ContextMenu
						{...contextMenuProps}
						actions={selectionActions}
						context={actionContext}
						trackingId="webdav-context-menu"
						title={computeContextMenuTitle(
							actionContext.selectedDocuments,
							(x) => x.name
						)}
					/>
				)}
			</DocumentGridViewContainer>
		);
	}
);

WebDavGridView.displayName = "WebDavGridView";

export type WebDavGridViewHandle = {
	resetScrollPosition(): void;
};